import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { previewInChunks } from './chunkedApi';
import { CleanView } from './CleanView';
import { CrmGrid, type CrmGridHost } from './CrmGrid';
import { crmGridCounts } from './crmGridModel';
import { CRM_COPY } from './copy';
import { crmSourceLabel } from '../../integrations/crmSourceLabel';
import { isInPreviewSelection, isMatched, MatchView } from './MatchView';
import { SendView } from './SendView';
import { hasQueueWork, isReady, queueWork, useCrmQueue } from './useCrmQueue';
import type { CrmQueueApi, LeadEditPatch, PersonMatchState, PersonPreview } from './types';
import type { CrmQueueState } from './useCrmQueue';

export interface CrmCounts {
  /** = latest match-check's `counts.needsEmail` — the Clean queue's size. The
   *  opening check always runs, so this is known on every draw.
   *
   *  In grid mode (`grid` below; port plan D9) it is the leads waiting on
   *  Clean instead — no email OR held by lead rules, each person once — and
   *  `null` ("not known") until both the check and the lead rows have
   *  landed. The legacy views always report a number, exactly as before. */
  clean: number | null;
  /**
   * The number of people the check found IN the CRM — `existing + ambiguous`
   * (gh-31 D4). It is the size of Match's left-hand "Already in your CRM"
   * column, not of everything Match lists: New rows are drawn on the right
   * and counted on that column's own header.
   *
   * Known on a cold draw, at no CRM cost, off the opening match check's own
   * counts — BUT ONLY when that check reports no `needs_email` rows.
   * Match-check buckets an emailless person `needs_email` unconditionally,
   * before it ever consults the CRM's links, so it cannot rule out that one
   * of them is already linked (e.g. from an earlier Send) — the live read's
   * id-property walk can bucket that same person `existing`/`ambiguous`,
   * which `isMatched` correctly counts once it lands. Trusting the cold
   * `existing + ambiguous` in that situation would make the tab number jump
   * upward the instant the preview resolves — the exact gh-31 D4 defect —
   * so it goes `null` ("not known") instead of guessing. Once the live read
   * HAS landed it always takes over as the source, both because it is the
   * fresher answer and because it is the one on screen (it re-buckets
   * against the CRM). `null` also covers the ordinary case: the check itself
   * has yet to land at all. Either way `null` means "not known", NOT "none"
   * — the parent renders the tab bare rather than inventing a `(0)`. A
   * counted zero is a real answer.
   */
  match: number | null;
  /**
   * = preview rows currently `isReady` — the Send queue's size. `isReady`
   * reads only `bucket` now (spec 1 §3's owner fallback chain means an
   * owner is never actually missing by the time a row is previewed — see
   * `useCrmQueue.isReady`), but the cheap check's bucket still isn't the
   * CRM's final word: the live read is the bucketing authority (see
   * `match`'s note above) and can re-bucket a row the check called
   * `needs_email`. So this waits for the preview the same way `match` does
   * — but unconditionally, with no cold exception — rather than risking
   * the same jump-on-preview defect gh-31 D4 was written to prevent. `null`
   * until the preview has landed — "not known", not "none".
   */
  send: number | null;
  /**
   * The number of DISTINCT people who are needs-an-email, needs-an-owner, or
   * both — "what's worth a look", for the rail badge (reworded p3 Task 8:
   * this is no longer "what blocks Send", which nothing here does any more).
   * Distinct, not a sum: since Task 2 put emailless people into the preview
   * too, one lead can be in both sets, and adding the terms would count them
   * twice — see `badgeCount`'s derivation. Never `null`: before the preview runs it is
   * simply the needs-an-email term, and it grows to include the owner term
   * once Match or Send has been opened. The owner term mirrors `OwnerPicker`'s
   * D9 rule (`ownerRequired && (ownerProvenance === 'integration_account' ||
   * ownerUnverified === true)` — the `ownerRequired` gate matters: the
   * backend reports `ownerProvenance`/`ownerUnverified` on every row
   * regardless of whether it needs one, and `OwnerPicker` never warns on a
   * row it doesn't) and is derived live from `previews` (not the preview
   * response's frozen `summary`), so a refetch that resolves a row's owner
   * differently moves this in step with `send`. It does NOT react to
   * `state.ownerPicks` — an explicit pick doesn't change what the backend
   * reported about the row, any more than it clears `OwnerPicker`'s own
   * warning outline.
   *
   * In grid mode (port plan D9) it is no-email ∪ rule-held ∪ needs-a-decision
   * ∪ unresolved owner, de-duplicated by person; the last two once the live
   * read has landed.
   */
  badge: number;
  /**
   * Grid mode only: whether `badge` already includes the terms the live read
   * supplies (needs-a-decision, unresolved owner), so the rail's sentence
   * names only what the number counts. Absent from the legacy views.
   */
  badgeIncludesLiveRead?: boolean;
}

/**
 * Everything the stage strip's `CrmSourceControl` needs to draw and drive the
 * CRM picker from OUTSIDE this component — shaped to that control's props
 * exactly, so the parent can spread it without knowing anything about the
 * queue. `onRequestChange` is an ASK, not a command: the guard that decides
 * whether the switch costs the operator anything lives here, with the state
 * that would be discarded.
 */
export interface CrmSourceContext {
  sources: string[];
  value: string;
  canManage: boolean;
  onRequestChange: (next: string) => void;
}

export interface CrmViewsProps {
  conferenceId: string;
  api: CrmQueueApi;
  /**
   * Which of the parent's four Segmented options is currently selected.
   * Clean/Match/Send mount together and STAY mounted regardless of this value
   * (controller ruling, p3 Task 6) — it only switches which one is visible
   * (CSS `display`, never unmount), so SendView's local results panel and
   * every field/owner pick survive switching views.
   */
  view: CrmQueueState['view'];
  /**
   * Fires whenever the derived counts change so the parent can label the
   * Segmented options and the event-rail badge.
   */
  onCounts?: (counts: CrmCounts) => void;
  /**
   * Hands the parent this queue's manual re-check, once per mount, so the
   * stage Segmented's toolbar can own the Refresh control. Same plain-
   * invalidation semantics as calling it here would have — never `force`.
   */
  onRefreshReady?: (refresh: () => void) => void;
  /**
   * Hands the parent this queue's match-check re-run, once per mount: the
   * same plain invalidation Clean's own email fix uses (`onRowsChanged`), and
   * nothing else — never the live preview, never `force`. For changes made
   * OUTSIDE the queue that the match check reads (an inline grid edit of an
   * email), where the manual Refresh above would re-read the CRM for every
   * lead with an email once Match or Send has been opened.
   *
   * `editEpoch` is the inline-edit store's epoch the re-run is for: when a
   * re-check already went out at or after it (grid Clean re-checks the
   * moment its own email fix confirms), the burst needs no second one.
   */
  onRecheckReady?: (recheck: (editEpoch?: number) => void) => void;
  /**
   * Grid mode: hands the parent this queue's "the leads changed" mark, once
   * per mount, for changes made outside the queue that the live read reflects
   * (an assignment). It marks the cached preview stale WITHOUT fetching it:
   * the next visit to Match or Send re-runs it once, and on Match / Send the
   * toolbar says so with Refresh. A no-op outside grid mode.
   */
  onPreviewStaleReady?: (markStale: () => void) => void;
  /**
   * Publishes the queue's CRM scope so the parent's stage strip can render it.
   * Same upward-callback idiom as `onCounts` / `onRefreshReady`: the queue
   * stays here (CrmViews is keyed by conference, and that key is what resets
   * the queue on an event switch — lifting `useCrmQueue` into BatchScreen
   * would break that), and the parent renders what it is handed.
   */
  onSourceContext?: (ctx: CrmSourceContext) => void;
  /**
   * The unified CRM grid (port plan step 6). When given, Clean / Match / Send
   * render as lenses on the Leads grid (`CrmGrid`) instead of their own
   * layouts, and the tab numbers count the work waiting on each tab (D9).
   * The queries, the queue and every rule about who is sent are this
   * component's own, exactly as without it; the live read stays lazy (D8).
   * Absent (the console, the stakeholder view, a shell that didn't opt in) →
   * the legacy views, unchanged.
   */
  grid?: CrmGridHost;
}

/**
 * One failed query, said plainly, with the way back. The visible label stays
 * short ("Retry"); the accessible name carries the line it belongs to, so the
 * match-check and preview retries never present as two identical buttons
 * (same composition precedent as SendView's per-row Recheck).
 */
function CrmErrorLine({
  message,
  retryLabel,
  onRetry,
  testId,
}: {
  message: string;
  /** The Retry button's accessible name: what it retries. */
  retryLabel: string;
  onRetry: () => void;
  testId: string;
}) {
  return (
    <div data-testid={testId} style={{ padding: space.md }}>
      <Space direction="vertical" size={space.sm}>
        <Typography.Text type="danger">{message}</Typography.Text>
        <Button size="small" aria-label={retryLabel} onClick={onRetry}>
          {CRM_COPY.retryButton}
        </Button>
      </Space>
    </div>
  );
}

/**
 * Owns every query for the p3 Clean/Match/Send flow and composes the three
 * views over them (p3 Task 6).
 *
 * The two queries are deliberately NOT peers. The match check runs at first
 * draw on whichever tab the operator is on, because it is cheap: the backend
 * answers it from its own record of earlier look-ups and reaches the CRM only
 * for people never checked before or whose email changed, so a routine re-open
 * of an unchanged event costs zero CRM queries. The preview is the expensive
 * one — a pure read that never uses that record, re-querying the CRM for every
 * lead with an email — so it is LAZY: nothing fires it until Match or Send is
 * opened, and once fired it stays available for the life of the mount
 * (`staleTime: Infinity`) so toggling between the tabs never re-reads.
 *
 * Otherwise the queries mount once (`staleTime: Infinity` — "recheck only via
 * invalidate/force") and re-fetch only on explicit invalidation: Clean's
 * `onRowsChanged` invalidates match-check (the backend's computed staleness
 * re-checks the fixed row); Send's `onCommitted` invalidates BOTH
 * match-check and preview, additionally flagging the next match-check call
 * `force: true` via a ref the queryFn closes over (cheap to wire, covers the
 * `changed_since_preview` edge case without a second network round-trip to
 * decide whether to force).
 *
 * `similar` enrichment: preview rows arrive with `similar: null` from the v1
 * backend, so match-check's OWN `similar` notes (fresher — "informational,
 * fresh checks only") are merged onto the previews handed to `MatchView`.
 *
 * A failed query is never allowed to read as an empty success (review fix
 * I3): a match-check failure replaces the whole views area with a plain-words
 * line + Retry; a preview failure replaces only Match and Send, leaving Clean
 * (which reads match-check, not the preview) fully usable. A failure that
 * still HAS data — a refetch that blipped — keeps the views up and says so
 * above them instead, so nothing already on screen is thrown away.
 *
 * The manual re-check (review fix I4) re-runs both queries by plain
 * invalidation — a manual recheck trigger per spec §6, never `force`. On a
 * queue where Match and Send were never opened, invalidating the preview is a
 * no-op (the query is disabled): Refresh re-checks, and does not smuggle the
 * live read back in behind the laziness. It is
 * published upward through `onRefreshReady` rather than rendered here, so the
 * `Refresh` control can sit in the stage Segmented's toolbar instead of
 * orphaned above (and, on the Leads tab, below) the queue.
 */
export function CrmViews({
  conferenceId,
  api,
  view,
  onCounts,
  onRefreshReady,
  onRecheckReady,
  onPreviewStaleReady,
  onSourceContext,
  grid,
}: CrmViewsProps) {
  const queryClient = useQueryClient();
  const { state, dispatch } = useCrmQueue(api.connectedSources[0]);
  // antd's App context, not the static `Modal.confirm`: the static call mounts
  // its own root OUTSIDE the `ConfigProvider` both shells drive, so a dark-mode
  // operator would get a confirm painted in antd's default light tokens
  // (same reason `@viq-admin/ui`'s `useConfirmAction` exists — which doesn't
  // fit here: it has no `cancelText` and requires an async `onConfirm`).
  const { modal } = App.useApp();

  // `requestSourceChange`'s pre-check has to read the CURRENT queue state at
  // the moment the operator requests the switch — a snapshot from whenever
  // the callback was last built would produce a wrong "clears N owner picks"
  // count, or skip the confirm entirely, once picks land after that build.
  // Held in a ref (updated every render, same idiom as `refreshRef` below)
  // rather than a `useCallback` dep on `state`, because `state` changes on
  // EVERY dispatch — owner picks, field picks, Send checkbox toggles — and a
  // dep on it would rebuild this callback, and therefore re-fire the publish
  // effect below and re-render all of `BatchScreen`, on every one of those
  // queue-level interactions.
  const stateRef = useRef(state);
  stateRef.current = state;

  // The switch guard lives here because dirtiness is the queue's business: the
  // control asks, this decides. `setSource` resets the queue by design (the
  // other CRM's rows are different rows), so an operator mid-triage must be
  // told what that costs — and told nothing at all when it costs nothing.
  const requestSourceChange = useCallback(
    (next: string) => {
      const current = stateRef.current;
      if (next === current.source) return;
      const work = queueWork(current);
      if (!hasQueueWork(work)) {
        dispatch({ type: 'setSource', source: next });
        return;
      }
      // Display only: the confirm's title/body take the pretty name so the
      // operator reads "Switch to HubSpot?", not "Switch to hubspot?" — `next`
      // itself, passed to `dispatch` below, stays the raw id.
      modal.confirm({
        title: CRM_COPY.switchSourceTitle(crmSourceLabel(next)),
        content: CRM_COPY.switchSourceBody(crmSourceLabel(next), CRM_COPY.switchSourceParts(work)),
        okText: CRM_COPY.switchSourceOk,
        cancelText: CRM_COPY.switchSourceCancel,
        onOk: () => dispatch({ type: 'setSource', source: next }),
      });
    },
    [dispatch, modal],
  );

  // Republish whenever the scope changes. The parent stores what it is handed
  // in state, so an effect that re-fires on every parent render would loop
  // against that setState — and `api.connectedSources` is an ARRAY whose
  // identity a parent re-render can change without its contents changing at
  // all. So the dep is the joined key, never the array: these deps are the
  // values that actually move. `requestSourceChange` is deliberately NOT a
  // dep — it now only changes identity if `dispatch`/`modal` do — so a queue
  // interaction (an owner pick, a field pick, a Send checkbox toggle) can no
  // longer re-fire this effect on its own.
  const sourcesKey = api.connectedSources.join('\0');
  useEffect(() => {
    onSourceContext?.({
      sources: api.connectedSources,
      value: state.source,
      canManage: api.canManage,
      onRequestChange: requestSourceChange,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesKey, state.source, api.canManage]);

  // Keep the reducer's own `view` field in sync with the parent-controlled
  // Segmented selection — informational (nothing here reads it back), but it
  // keeps `useCrmQueue`'s state truthful for anything that inspects it later
  // (e.g. `resetAfterSend`'s "keeps the current view" contract).
  useEffect(() => {
    if (view !== state.view) dispatch({ type: 'setView', view });
  }, [view, state.view, dispatch]);

  // Has anyone asked for the expensive half yet? The match check is cheap —
  // the backend answers it from its own record of earlier look-ups and reaches
  // the CRM only for people never checked or whose email changed — so it stays
  // automatic. The preview is the opposite: a pure read that deliberately
  // never uses that record, so it re-queries the CRM for EVERY lead with an
  // email, every time. On a 300-lead conference that is several CRM queries per
  // page load, per operator, for a view the operator may never open. So it is
  // latched to the first moment Match or Send is actually opened, and stays on
  // for the life of this mount (a conference switch remounts, via the parent's
  // `key`).
  //
  // Set during render, not in an effect: an effect would leave the very first
  // Match draw with the query still disabled, painting one frame of "Nobody
  // new" before the real loading state. Setting state during a component's own
  // render is React's documented way to derive from a prop.
  const wantsPreview = view === 'match' || view === 'send';
  const [previewRequested, setPreviewRequested] = useState(wantsPreview);
  if (wantsPreview && !previewRequested) setPreviewRequested(true);

  const matchCheckKey = ['crmMatchCheck', conferenceId, state.source] as const;
  // Set by `handleCommitted` just before invalidating; read (and cleared) by
  // the very next `matchCheck` call so only that ONE refetch forces past the
  // backend's computed staleness — everything else (mount, Clean's
  // onRowsChanged) stays a plain check.
  const forceNextMatchCheckRef = useRef(false);

  const matchQ = useQuery({
    queryKey: matchCheckKey,
    queryFn: async () => {
      const force = forceNextMatchCheckRef.current;
      const result = await api.matchCheck(
        conferenceId,
        state.source,
        force ? { force: true } : undefined,
      );
      // Clear only on SUCCESS — a failed forced fetch (react-query retries,
      // or a later manual refetch) must still see `force: true` on its next
      // attempt, rather than silently dropping back to an unforced check.
      if (force) forceNextMatchCheckRef.current = false;
      return result;
    },
    staleTime: Infinity,
  });

  const results: PersonMatchState[] = matchQ.data?.results ?? [];

  // Widened past "what Match draws": the preview selection also asks about
  // `needs_email` match-check rows (`isInPreviewSelection`), because the
  // preview — not match-check — is the authority on bucketing and answers
  // for emailless people too (`emailChecked: false`, bucketed `new` on no
  // link found or `existing`/`ambiguous` on a real one — see
  // `isInPreviewSelection`'s doc), carrying their notes and tasks so Send can
  // reach them. Match-check keeps bucketing them `needs_email` regardless —
  // Clean and the rail badge read that bucket straight off match-check and
  // must keep seeing it — so this widening is preview-selection-only and
  // leaves `isInMatchQueue` (what Match draws FROM THE PREVIEW response)
  // untouched. It DOES mean the cold `matchCount` below cannot always trust
  // match-check's own `existing + ambiguous` — see that derivation's comment.
  const checkedIds = useMemo(
    () => results.filter(isInPreviewSelection).map((r) => r.personId),
    [results],
  );

  const similarByPersonId = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const r of results) m.set(r.personId, r.similar);
    return m;
  }, [results]);

  const previewQ = useQuery({
    // `state.previewScope` is part of the key on purpose (spec 4 §3, p3 Task
    // 5): switching to 'all' via the Match disclosure's "show them" is a
    // RE-REQUEST, not a client-side filter over what 'changed' already
    // returned — the backend decides membership under each scope, so a scope
    // change must always land as a fresh fetch rather than serving the
    // 'changed' response's cache.
    queryKey: ['crmPreview', conferenceId, state.source, checkedIds.join(','), state.previewScope],
    // Through the body-cap chunker, never `api.preview` directly — the
    // match-check above is uncapped on its no-personIds conference path, so
    // `checkedIds` can exceed what one preview body may carry.
    queryFn: () => previewInChunks(api, conferenceId, state.source, checkedIds, state.previewScope),
    // `previewRequested` is the lazy gate; a disabled query still SERVES its
    // cache, so a second visit to a conference already previewed this session
    // hands its rows (and counts) straight back without another CRM read.
    //
    // Grid mode also runs it only while Match or Send is showing: a preview
    // marked stale by an edit, a move or a bulk run (`markPreviewStale`) —
    // or one whose people changed — is re-run once, on entering Match or
    // Send, never in the background; and never on window focus or
    // reconnect. Entering with a fresh preview reads nothing.
    enabled: checkedIds.length > 0 && previewRequested && (!grid || wantsPreview),
    ...(grid ? { refetchOnWindowFocus: false, refetchOnReconnect: false } : {}),
    staleTime: Infinity,
  });

  // Preview responses arrive with `similar: null` (v1 backend) — fall back to
  // match-check's own note for the same person, informational-only either way.
  const previews: PersonPreview[] = useMemo(
    () =>
      (previewQ.data?.previews ?? []).map((p) => ({
        ...p,
        similar: p.similar ?? similarByPersonId.get(p.personId) ?? null,
      })),
    [previewQ.data, similarByPersonId],
  );

  // Grid mode only: every active lead row, for Clean's rule-held term (D9) —
  // known on open, without the live read. Postgres only; no CRM traffic. The
  // screen's one load of them (`useGridLeadRows`), which the lenses render too.
  const gridLeadRows = grid?.rows.data?.leads;

  const ownersQ = useQuery({
    queryKey: ['crmOwners', state.source],
    queryFn: () => api.fetchOwners(state.source),
    staleTime: 10 * 60_000,
    // `retry: false` to agree with BatchScreen's observer on this same key
    // (the Assigned-to suggestions query, 2026-10-02): TanStack v5 lets the
    // LAST-mounted observer's fetch options win for a shared key, so two
    // observers disagreeing here would make which one mounted last decide
    // whether a failed roster fetch retries — this view should never retry
    // either, same reasoning as BatchScreen's own comment on that query.
    retry: false,
  });
  const owners = ownersQ.data ?? [];

  function invalidateMatchCheck() {
    void queryClient.invalidateQueries({ queryKey: matchCheckKey });
  }
  function invalidatePreview() {
    // Partial key (no ids suffix) so this catches the query regardless of
    // which `checkedIds` snapshot it was fetched under.
    void queryClient.invalidateQueries({ queryKey: ['crmPreview', conferenceId, state.source] });
  }
  /**
   * Grid mode: the leads changed since the live read (an edit, a move, a
   * bulk run): mark the preview stale WITHOUT fetching it (D8). Entering
   * Match or Send re-runs it once; while on them the toolbar says so, with
   * Refresh. A no-op otherwise, and on a preview never read.
   */
  function markPreviewStale() {
    if (!grid) return;
    void queryClient.invalidateQueries({
      queryKey: ['crmPreview', conferenceId, state.source],
      refetchType: 'none',
    });
  }
  /** Grid mode: re-read the lead rows (the held count, every lens). A no-op otherwise. */
  function invalidateGridLeads() {
    grid?.rows.reload();
  }
  // The inline-edit store's epoch the last match re-check covered: a
  // re-check issued after a save confirmed reads it.
  const recheckedThroughRef = useRef<number | null>(null);
  /**
   * Re-run the match check for an edit — nothing else (legacy Clean's
   * `onRowsChanged`). With `editEpoch`, skipped when a re-check already went
   * out for that epoch or a later one, so a grid Clean fix and the edit burst
   * it is part of re-check once.
   */
  function recheckAfterEdit(editEpoch?: number) {
    // Whatever was edited, the live read may say otherwise now (a hold
    // lifted, an email added): marked stale for the next Match / Send visit.
    markPreviewStale();
    if (editEpoch !== undefined) {
      const through = recheckedThroughRef.current;
      if (through !== null && through >= editEpoch) return;
      recheckedThroughRef.current = editEpoch;
    }
    invalidateMatchCheck();
  }
  function handleCommitted() {
    forceNextMatchCheckRef.current = true;
    invalidateMatchCheck();
    invalidatePreview();
    invalidateGridLeads();
  }
  /** Manual re-check (spec §6). Plain invalidation — never `force`, which is
   *  reserved for the post-commit drift case. */
  function handleRefresh() {
    invalidateMatchCheck();
    invalidatePreview();
    invalidateGridLeads();
  }
  /** Details drawer's in-place edit (gh-31): save through the same composed
   *  lead-edit call Clean uses, then re-read BOTH queries so the comparison
   *  (and the buckets, if an email changed) come back fresh. Plain
   *  invalidation — not `force`; nothing was committed. */
  async function handleEditLead(personId: string, patch: LeadEditPatch) {
    await api.updateLeadFields(personId, patch);
    invalidateMatchCheck();
    invalidatePreview();
    invalidateGridLeads();
  }
  /** Grid mode: the lead drawer closed on a CRM tab. Whatever was edited
   *  there (an email, most often) is re-checked the cheap way — the match
   *  check, which reaches the CRM only for changed emails. (The screen
   *  re-reads the lead rows, once, for every drawer close.) Never the live
   *  read: that waits for Refresh. */
  function handleGridLeadDetailClosed() {
    invalidateMatchCheck();
  }

  // Published ONCE per mount (the parent stores it in a ref and renders the
  // control), through an indirection that is reassigned every render — so the
  // parent's handle always runs THIS render's closure over `conferenceId` /
  // `state.source`, never the one captured at mount.
  const refreshRef = useRef(handleRefresh);
  refreshRef.current = handleRefresh;
  // The match-check-only re-run, published the same way (once per mount,
  // through a ref reassigned every render).
  const recheckRef = useRef(recheckAfterEdit);
  recheckRef.current = recheckAfterEdit;
  const markStaleRef = useRef(markPreviewStale);
  markStaleRef.current = markPreviewStale;
  // Grid mode: every confirmed field or note save (any table, any tab) marks
  // the live read stale the moment it lands — before the edit burst's match
  // re-check, so a visit to Send right after an inline fix re-reads.
  const gridLeadSync = grid?.leadSync;
  const gridNoteSync = grid?.noteSync;
  useEffect(() => {
    const offFields = gridLeadSync?.onConfirmed(() => markStaleRef.current());
    const offNotes = gridNoteSync?.onConfirmed(() => markStaleRef.current());
    return () => {
      offFields?.();
      offNotes?.();
    };
  }, [gridLeadSync, gridNoteSync]);
  useEffect(() => {
    onRefreshReady?.(() => refreshRef.current());
    onRecheckReady?.((editEpoch) => recheckRef.current(editEpoch));
    onPreviewStaleReady?.(() => markStaleRef.current());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Counts, exposed upward (render-prop/callback) ────────────────────────
  const matchCounts = matchQ.data?.counts;
  const cleanCount = matchCounts?.needsEmail ?? 0;
  const previewLoaded = previewQ.data !== undefined;
  // Match costs nothing extra to count, MOST of the time: the opening check
  // already buckets the roster, and the people it found in the CRM
  // (`existing + ambiguous`, the same `isMatched` the view's left column is
  // built from) are the tab's number. But that cold count can only be
  // trusted when the check reports no `needs_email` rows. Match-check
  // buckets an emailless person `needs_email` unconditionally, before it
  // ever asks whether they're already linked — so a `needs_email` person can
  // still be `existing`/`ambiguous` once the live read's id-property walk
  // finds their real link (fix round 1: reachable on the ordinary path, e.g.
  // right after a Send creates that very link and `handleCommitted` re-runs
  // both queries). Counting `existing + ambiguous` cold in that situation
  // would under-count versus what lands moments later — the tab number
  // ticking up right after the preview is exactly the gh-31 D4 defect this
  // whole label exists to prevent. So: any `needs_email` row present means
  // the cold count is refused (`null`, "not known yet") rather than guessed.
  // Once the live read has landed it is both fresher and the thing on
  // screen, so the previews are counted instead, unconditionally — keeping
  // the label equal to the left column's rows rather than to the older
  // check, for every roster shape.
  const matchCount = useMemo(() => {
    if (matchCounts === undefined) return null;
    if (previewLoaded) return previews.filter(isMatched).length;
    if (matchCounts.needsEmail > 0) return null;
    return matchCounts.existing + matchCounts.ambiguous;
  }, [matchCounts, previewLoaded, previews]);
  // No longer reads `state.ownerPicks` — `isReady` stopped testing owner
  // presence (spec 1 §3's fallback chain always resolves one), so an owner
  // pick can no longer move this count.
  const sendCount = useMemo(
    () => (previewLoaded ? previews.filter(isReady).length : null),
    [previewLoaded, previews],
  );
  // p3 Task 8: redefined to mirror `OwnerPicker`'s D9 warning rule instead of
  // re-deriving one. The old test — `ownerRequired && !ownerPicks && !
  // suggestedOwnerId` — was identical to `isReady`'s pre-Task-3 owner clause,
  // so once spec 1 §3's fallback chain started always resolving an owner it
  // sat at 0 forever, AND it excluded exactly the rows worth flagging: an
  // integration-account or unverified-default owner almost always carries a
  // non-null `suggestedOwnerId`. The rows that actually warrant a look are
  // `ownerProvenance === 'integration_account'` (the fallback ran out of
  // configured defaults) or `ownerUnverified === true` (a configured default
  // the backend couldn't confirm against the CRM's user list) — read straight
  // off each preview row, same as `OwnerPicker`'s own warning outline, and
  // for the same reason NOT reactive to `state.ownerPicks`: an explicit pick
  // doesn't change what the backend reported about the row, so it doesn't
  // clear this term any more than it clears `OwnerPicker`'s badge. Strict
  // `===`/`true` comparisons — both fields are optional deploy-skew (an
  // older backend's preview omits them entirely), and `undefined` must count
  // as nothing, never as a match. Derived from `previews` (not the preview
  // response's frozen `summary.integrationAccountOwned`/`ownerUnverified`
  // counters), so a refetch that changes a row's resolution moves this term
  // — but, unlike before Task 8, a same-session owner pick alone does not.
  //
  // `p.ownerRequired &&` (fix round 1, Critical): the backend computes
  // `ownerProvenance`/`ownerUnverified` UNCONDITIONALLY for every row, "even
  // for a row that doesn't strictly need one" (viq-backend's
  // `preview.ts`) — an `existing` row already owned by a real human still
  // gets `ownerProvenance: 'integration_account'` if the capturing rep
  // didn't match and no default is configured; that provenance value
  // describes what the fallback chain WOULD have resolved, not what this
  // row needs. `OwnerPicker` — this rule's source of truth — never shows a
  // warning for such a row: it returns plain read-only text and skips the
  // D9 check entirely when `!ownerRequired` (`OwnerPicker.tsx`'s early
  // return). So the D9 rule as actually implemented is `ownerRequired &&
  // (isIntegrationAccount || isUnverified)`, and the badge must gate on it
  // too or it inflates "worth a look" with rows nobody would ever see
  // flagged. No separate bucket check is needed alongside it: the backend
  // derives `ownerRequired` as `bucket === 'new' || (bucket === 'existing'
  // && (matchOwnerId === null || matchOwnerId === integrationUserId))`, so
  // it is already false for every `ambiguous` row (and for any `existing`
  // row that doesn't need one) — `ownerRequired` alone fully subsumes a
  // bucket filter, and re-adding one here would be redundant, not
  // protective. THIS FILTER AND `OwnerPicker`'s WARNING MUST STAY IN SYNC —
  // that agreement is the entire point of "mirrors the D9 rule," not a
  // one-time coincidence.
  const unresolvedOwnerIds = useMemo(
    () =>
      previews
        .filter(
          (p) => p.ownerRequired && (p.ownerProvenance === 'integration_account' || p.ownerUnverified === true),
        )
        .map((p) => p.personId),
    [previews],
  );
  // The people match-check says have no email — the same set `cleanCount`
  // counts, but as IDS, because the badge below has to de-duplicate against
  // them rather than add to them.
  const needsEmailIds = useMemo(
    () => new Set(results.filter((r) => r.bucket === 'needs_email').map((r) => r.personId)),
    [results],
  );
  // Only ever what is KNOWN: needs-an-email on a cold draw (the preview
  // hasn't run, so `previews` is empty and the owner term contributes
  // nothing), growing to needs-an-email PLUS needs-an-owner once Match or
  // Send has been opened. It is never suppressed while the preview is
  // missing — a lead with no email is still worth a look whether or not
  // anyone opened Match.
  //
  // DISTINCT PEOPLE, not a sum of two counts (fix round 2, Important). The
  // two terms could not overlap before this branch: emailless people were
  // excluded from the preview selection entirely, so nobody was in both.
  // Task 2 removed that separation — `isInPreviewSelection` now includes
  // `needs_email` rows, and the preview answers for them (matched by the
  // VendoIQ id property, `emailChecked: false`), typically bucketing them
  // `new` with `ownerRequired: true`. With no configured defaults the owner
  // chain then lands on `integration_account`, and one lead was counted by
  // BOTH terms. That is not an edge case: spec 1 §1 has no-email as the
  // majority initial state on badge-OCR conferences and no tenant has
  // defaults configured, so summing could report a badge larger than the
  // conference's entire lead count.
  //
  // Written as `cleanCount + (owner ids not already counted)` rather than as
  // one union of both id sets, deliberately: `cleanCount` stays the
  // backend's own `counts.needsEmail` — the number the Clean tab's label
  // shows — so the badge cannot drift from the tab beside it, and a cold
  // draw still reports exactly `cleanCount` no matter what `results` holds.
  const badgeCount = useMemo(
    () => cleanCount + unresolvedOwnerIds.filter((id) => !needsEmailIds.has(id)).length,
    [cleanCount, unresolvedOwnerIds, needsEmailIds],
  );

  const overwriteLocked = previewQ.data?.overwriteAllowed === false;

  // Grid mode's tab numbers (port plan D9): the work waiting on each tab.
  // Same inputs as the counts above plus the lead rows; null wherever an
  // input isn't in yet, so no tab claims a number nobody has counted.
  const gridCounts = useMemo(
    () =>
      grid
        ? crmGridCounts({
            results: matchQ.data?.results,
            leadRows: gridLeadRows,
            previews,
            previewLoaded,
            state,
            overwriteLocked,
          })
        : null,
    [grid, matchQ.data, gridLeadRows, previews, previewLoaded, state, overwriteLocked],
  );
  const gridHeldCount = useMemo(
    () =>
      gridLeadRows ? gridLeadRows.filter((l) => l.completeness?.sendBlocked === true).length : null,
    [gridLeadRows],
  );

  // `onCounts` is deliberately OUT of the deps: the parent stamps each report
  // with the conference it belongs to, which means passing an inline closure
  // whose identity changes every parent render. Adding it here would re-fire
  // this on every parent render and loop against that setState. Report on the
  // count VALUES only.
  useEffect(() => {
    if (gridCounts) {
      onCounts?.({
        clean: gridCounts.clean,
        match: gridCounts.match,
        send: gridCounts.send,
        badge: gridCounts.badge,
        badgeIncludesLiveRead: gridCounts.badgeIncludesLiveRead,
      });
      return;
    }
    onCounts?.({ clean: cleanCount, match: matchCount, send: sendCount, badge: badgeCount });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cleanCount,
    matchCount,
    sendCount,
    badgeCount,
    gridCounts?.clean,
    gridCounts?.match,
    gridCounts?.send,
    gridCounts?.badge,
    gridCounts?.badgeIncludesLiveRead,
  ]);

  const tenantPolicy = previewQ.data?.policy ?? 'fillBlanksOnly';
  // What this run would ACTUALLY commit under — the per-run toggle when set,
  // else the tenant default carried by the preview response. Left undefined
  // until the preview lands so Match displays the safe keep-CRM default rather
  // than guessing (review fix C1).
  const effectivePolicy = state.policyOverride ?? previewQ.data?.policy;

  // A failed query with NO data is the case that used to masquerade as an
  // empty success — it replaces the views it feeds. A failed REFETCH that
  // still has data keeps the (stale) views up, with the failure said out loud
  // above them, so nothing already on screen — Send's results panel included —
  // is destroyed by a network blip.
  const matchBlocking = matchQ.isError && matchQ.data === undefined;
  const previewBlocking = previewQ.isError && previewQ.data === undefined;
  // The live read behind Match and Send is on its way and there is nothing to
  // draw yet — either the opening check that feeds it hasn't landed, or the
  // read itself is in flight. Before the preview went lazy this window was
  // invisible (it had almost always closed before anyone opened Match); now it
  // is the FIRST thing an operator sees there, so both views substitute a
  // "still looking" line for their empty states rather than announcing, quite
  // wrongly, that there is nobody.
  const previewLoading =
    (matchQ.data === undefined && !matchQ.isError) ||
    (previewRequested &&
      checkedIds.length > 0 &&
      previewQ.data === undefined &&
      !previewQ.isError);
  // Grid mode, on Match or Send: the live read on screen was marked stale
  // (the leads changed since) and isn't being re-run — the toolbar says so,
  // with Refresh, rather than fetching on its own.
  const previewStale =
    !!grid && wantsPreview && previewQ.data !== undefined && previewQ.isStale && !previewQ.isFetching;
  const matchErrorLine = matchQ.isError ? (
    <CrmErrorLine
      message={CRM_COPY.matchCheckFailedLine}
      retryLabel={CRM_COPY.retryMatchCheckLabel}
      onRetry={() => void matchQ.refetch()}
      testId="crm-match-error"
    />
  ) : null;
  const previewErrorLine = previewQ.isError ? (
    <CrmErrorLine
      message={CRM_COPY.previewFailedLine}
      retryLabel={CRM_COPY.retryPreviewLabel}
      onRetry={() => void previewQ.refetch()}
      testId="crm-preview-error"
    />
  ) : null;

  if (grid) {
    // The grid sits exactly where the Leads grid does (it brings its own
    // gutters), so only the lines above it take the page's padding. Those
    // lines are the legacy ones, placed as they always were.
    return (
      <div data-testid={`crm-views-${conferenceId}`}>
        <div style={{ padding: `0 ${space.lg}px` }}>
          {!api.canManage && (
            <Alert
              data-testid="crm-read-only-banner"
              type="info"
              showIcon
              message={CRM_COPY.help.readOnly.text}
              style={{ marginBottom: space.md }}
            />
          )}
          {matchErrorLine}
          {/* Clean doesn't read the preview, so a failed live read is said on
              Match and Send only — unless a refetch failed with rows still on
              screen, which is worth saying wherever the operator is. */}
          {previewErrorLine && (!previewBlocking || view === 'match' || view === 'send') && previewErrorLine}
        </div>
        <CrmGrid
          // A different CRM is a different queue (`setSource` resets it): its
          // outcomes, filter and Clean notes start over with it.
          key={state.source}
          view={view}
          conferenceId={conferenceId}
          api={api}
          source={state.source}
          results={results}
          resultsUpdatedAt={matchQ.dataUpdatedAt}
          previews={previews}
          previewLoaded={previewLoaded}
          previewLoading={previewLoading}
          previewFailed={previewBlocking}
          summary={previewQ.data?.summary}
          owners={owners}
          state={state}
          dispatch={dispatch}
          tenantPolicy={tenantPolicy}
          effectivePolicy={effectivePolicy}
          overwriteLocked={overwriteLocked}
          heldCount={gridHeldCount}
          onEditLead={handleEditLead}
          onEmailFixSaved={recheckAfterEdit}
          previewStale={previewStale}
          onRefresh={handleRefresh}
          onLeadDetailClosed={handleGridLeadDetailClosed}
          onCommitted={handleCommitted}
          host={grid}
          hidden={matchBlocking}
        />
      </div>
    );
  }

  return (
    // `space.lg` is the Batch page's gutter — the same 16px every block above
    // this one sets for itself. Without it the queue rows render flush against
    // the scroll container while the tiles above them are inset.
    <div
      data-testid={`crm-views-${conferenceId}`}
      style={{ padding: `0 ${space.lg}px ${space.lg}px` }}
    >
      {/* Every control on all three tabs is disabled without
          `manage:CrmIntegration`, and nothing on screen used to say so. An
          Alert, not a tooltip: an explanation for why a whole surface is dead
          must not need a hover to discover. */}
      {!api.canManage && (
        <Alert
          data-testid="crm-read-only-banner"
          type="info"
          showIcon
          message={CRM_COPY.help.readOnly.text}
          style={{ marginBottom: space.md }}
        />
      )}
      {/* A failed REFETCH keeps its (stale) views — the line rides above them. */}
      {!matchBlocking && matchErrorLine}
      {!previewBlocking && previewErrorLine}

      {matchBlocking ? (
        matchErrorLine
      ) : (
        <>
          <div style={{ display: view === 'clean' ? 'block' : 'none' }}>
            <CleanView
              conferenceId={conferenceId}
              api={api}
              source={state.source}
              results={results}
              // Completed-fetch timestamp: a re-check that did NOT graduate a
              // saved row returns byte-identical results, so this advancing is
              // Clean's only way to see the refetch landed and un-stick it.
              resultsUpdatedAt={matchQ.dataUpdatedAt}
              onRowsChanged={invalidateMatchCheck}
            />
          </div>
          {previewBlocking ? (
            // Match and Send both live on the preview; Clean above does not,
            // so it stays fully functional through a preview failure.
            <div style={{ display: view === 'match' || view === 'send' ? 'block' : 'none' }}>
              {previewErrorLine}
            </div>
          ) : (
            <>
              <div style={{ display: view === 'match' ? 'block' : 'none' }}>
                <MatchView
                  previews={previews}
                  owners={owners}
                  canManage={api.canManage}
                  fieldChoices={state.fieldChoices}
                  ownerPicks={state.ownerPicks}
                  effectivePolicy={effectivePolicy}
                  tenantPolicy={tenantPolicy}
                  summary={previewQ.data?.summary}
                  // The scope the rows above were fetched under — the delta
                  // disclosure is gated on it, because the backend reports
                  // `summary.collapsed` under `scope: 'all'` too.
                  previewScope={state.previewScope}
                  overwriteLocked={previewQ.data?.overwriteAllowed === false}
                  loading={previewLoading}
                  onEditLead={handleEditLead}
                  active={view === 'match'}
                  dispatch={dispatch}
                />
              </div>
              <div style={{ display: view === 'send' ? 'block' : 'none' }}>
                <SendView
                  conferenceId={conferenceId}
                  api={api}
                  source={state.source}
                  previews={previews}
                  state={state}
                  dispatch={dispatch}
                  canManage={api.canManage}
                  tenantPolicy={tenantPolicy}
                  summary={previewQ.data?.summary}
                  loading={previewLoading}
                  onCommitted={handleCommitted}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
